import { translate } from '@teamleader/i18n';
import { IconChevronDownSmallOutline, IconChevronUpSmallOutline } from '@teamleader/ui-icons';
import React, { Fragment, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useIsSmall } from '../../../../Components/Context/Hooks/mediaQueries';
import Box, { BoxProps } from '../../../../Components/UI/Box';
import { MenuItem, MenuTitle } from '../../../../Components/UI/DropdownButton';
import Popover from '../../../../Components/UI/Popover';
import Spinner from '../../../../Components/UI/Spinner';
import { EVENTS, track } from '../../../../Utils/analytics';
import { InvoiceContext } from '../../Context';
import { useInvoicesListResult } from '../../Types/Invoices/useInvoicesList';
import styles from '../styles.css';

const InvoiceName: React.FC<BoxProps> = () => {
  const { token } = useParams<HomeRouteProps>();
  const { invoice, openInvoices, paidInvoices, creditedInvoices } = useContext(InvoiceContext);
  const [isOpen, setIsOpen] = useState(false);
  const isSmall = useIsSmall();
  const ref = useRef<HTMLButtonElement>(null);

  const handleOpenDropdown = useCallback(() => setIsOpen(true), []);
  const handleCloseDropdown = useCallback(() => setIsOpen(false), []);

  const invoices = useMemo(() => {
    return [...(openInvoices!.data || []), ...(paidInvoices!.data || []), ...(creditedInvoices!.data || [])];
  }, [creditedInvoices, openInvoices, paidInvoices]);

  const title = translate('title.invoice', { invoiceNumber: invoice?.invoiceNumber ?? '' });
  const amount = invoices.length;
  const dropdownVisible = isSmall && amount > 1 && !!invoice;
  const Icon = isOpen ? IconChevronUpSmallOutline : IconChevronDownSmallOutline;

  const onInvoiceClick = (invoiceId: string) => {
    track(EVENTS.SELECTED_INVOICE, { invoice: invoiceId, fromHeader: true });
    handleCloseDropdown();
  };

  const menuSections: Array<[string, useInvoicesListResult]> = [
    [translate('openInvoices'), openInvoices!],
    [translate('paidInvoices'), paidInvoices!],
    [translate('creditedInvoices'), creditedInvoices!],
  ];

  return (
    <Box className={styles['title']}>
      <Box element="h1" display="flex" alignItems="center" onClick={isSmall ? handleOpenDropdown : undefined} ref={ref}>
        <span className={styles['title-text']} title={title}>
          {title}
        </span>
        {dropdownVisible && <Icon className={styles['caret']} />}
      </Box>
      {ref.current && isOpen && (
        <Popover
          anchor={ref.current}
          onEscKeyDown={handleCloseDropdown}
          onOverlayClick={handleCloseDropdown}
          placement="bottom-start"
        >
          <Box element="ul" marginVertical={2} className={styles['document-list']}>
            {menuSections.map(([title, invoices]) => {
              if (!invoices.data || invoices.data.length === 0) {
                return null;
              }

              return (
                <Fragment key={title}>
                  <MenuTitle title={title} />
                  {invoices.data.map((listInvoice) => (
                    <MenuItem
                      key={listInvoice.id}
                      element={Link}
                      to={`/${token || ''}/${listInvoice.id}`}
                      onClick={() => onInvoiceClick(listInvoice.id)}
                      selected={listInvoice.id === invoice?.id}
                      title={translate('title.invoice', { invoiceNumber: listInvoice.invoiceNumber })}
                    >
                      {translate('title.invoice', { invoiceNumber: listInvoice.invoiceNumber })}
                    </MenuItem>
                  ))}
                  {invoices.hasMore && (
                    <MenuItem onClick={invoices.loadMore} disabled={invoices.loading}>
                      {invoices.loading ? (
                        <Spinner className={styles['load-more-button-spinner']} />
                      ) : (
                        <>
                          {translate('actions.loadMore')}
                          <IconChevronDownSmallOutline className={styles['load-more-button-icon']} />
                        </>
                      )}
                    </MenuItem>
                  )}
                </Fragment>
              );
            })}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

export default InvoiceName;
